import { Alert, Button, ConfirmationDialog, Pill, Spinner, Stack, toast } from 'component-library';
import { lazy, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import {
  INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS,
  IndicatorProductSubscriptionConfiguration,
} from '@/api/rest/resources/types/IndicatorProduct';
import { SuspenseBoundary } from '@/components';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { Card } from '@/components/widgets/o/Card/Card';
import { useUploadInSituDataFile } from '@/hooks/useDashboardIndicatorProductSubscriptionConfiguration';
import { Logger } from '@/lib/logs/logger';
import { useCanManageInsituData } from '@/pages/shared/uploads/hooks/useCanManageInsituData';
import { resolveThemeColor } from '@/utils/resolveThemeColor';

import { useMediaMapDraft } from '../hooks/useMediaMapDraft';
import { useMediaMapItemsFromFacts } from '../hooks/useMediaMapItemsFromFacts';
import { buildMediaMapJsonFile } from '../utils/mediaMapItem';
import { MediaMapItemCard } from './MediaMapItemCard';

const MediaMapItemDialog = lazy(() => import('./MediaMapItemDialog'));

type MediaMapItemCardsProps = {
  configuration: IndicatorProductSubscriptionConfiguration;
};

export const MediaMapItemCards = ({ configuration }: MediaMapItemCardsProps) => {
  const { t } = useTranslation();

  return (
    <ErrorBoundary
      FallbackComponent={() => (
        <Card styles={{ root: 'rounded-3xl p-4' }}>
          <Stack spacing={4}>
            <span className='typography-body1'>{t('shared.mediaMaps.newConfig.labels.mediaMapUpload')}</span>
            <span className='typography-body1 text-text-disabled'>{t('global.widget.error.message')}</span>
          </Stack>
        </Card>
      )}
      onError={(error, info) => {
        Logger.error(`Media map item cards rendering error: ${error} ${info}`);
      }}
    >
      <SuspenseBoundary fallback={<Spinner size={10} />}>
        <MediaMapItemCardsBase configuration={configuration} />
      </SuspenseBoundary>
    </ErrorBoundary>
  );
};

const MediaMapItemCardsBase = ({ configuration }: MediaMapItemCardsProps) => {
  const { t } = useTranslation();
  const hydratedItems = useMediaMapItemsFromFacts(configuration);
  const canManageInsituData = useCanManageInsituData();

  const {
    mediaMapDraft,
    isDraftMediaMap,
    addItemToMediaMapDraft,
    updateItemInMediaMapDraft,
    removeItemFromMediaMapDraft,
    clearMediaMapDraft,
  } = useMediaMapDraft(configuration.id, hydratedItems);

  const { submit, isSubmitting } = useUploadInSituDataFile();
  const [hasJustSubmitted, setHasJustSubmitted] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);

  if (
    hasJustSubmitted &&
    (
      [
        INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.VALIDATED,
        INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.ERROR,
      ] as string[]
    ).includes(configuration.status)
  ) {
    // Clear the post-submit flag once the server processing changes -> configuration status changes to validated/error
    setHasJustSubmitted(false);
    clearMediaMapDraft();
  }

  const handleSubmit = () => {
    const file = buildMediaMapJsonFile(mediaMapDraft);
    submit(
      { file, configurationId: configuration.id },
      () => {
        setHasJustSubmitted(true);
        toast({
          type: 'success',
          title: t('shared.insituDataUpload.toasts.uploadSuccess.title'),
          description: t('shared.insituDataUpload.toasts.uploadSuccess.description'),
        });
      },
      () =>
        toast({
          type: 'error',
          title: t('shared.insituDataUpload.toasts.uploadError.title'),
          description: t('shared.insituDataUpload.toasts.uploadError.description'),
        }),
    );
  };

  const formStatus = getFormStatusFromConfiguration(configuration, isDraftMediaMap, hasJustSubmitted);
  const validationErrors = configuration.validation_errors ?? [];

  return (
    <Stack spacing={6} className='w-full'>
      <Stack direction='row' className='w-full items-center justify-between'>
        <MediaMapStatusPill status={formStatus} />
        <Stack direction='row' spacing={6}>
          {isDraftMediaMap && !hasJustSubmitted && (
            <Button
              variant='outline'
              onClick={() => setShowClearDialog(true)}
              data-testid='media-map-clear-button'
              className='bg-error hover:bg-error-dark text-white-100 border-error hover:border-error-dark'
            >
              <i className='ri-delete-bin-line' />
              {t('shared.mediaMaps.configuration.buttons.clearChanges')}
            </Button>
          )}
          {canManageInsituData && (
            <Button
              variant='outline'
              className='w-fit'
              onClick={() => setShowAddDialog(true)}
              data-testid='media-map-add-button'
              disabled={hasJustSubmitted}
            >
              <i className='ri-add-line' />
              {t('shared.mediaMaps.configuration.buttons.addMedia')}
            </Button>
          )}
        </Stack>
      </Stack>
      {hasJustSubmitted && (
        <Alert data-testid='media-map-processing-banner'>
          <i className='ri-information-line' />
          {t('shared.mediaMaps.configuration.processingBanner')}
        </Alert>
      )}
      {validationErrors.length > 0 && (
        <Alert type='error' className='flex'>
          <Stack spacing={2} className='w-full'>
            <Stack direction='row' spacing={2} className='items-center'>
              <Icon icon='ri-error-warning-line' />
              <span className='typography-body1Semibold'>
                {t('shared.insituDataUpload.fileUpload.validationErrorsTitle')}
              </span>
            </Stack>
            <ul className='list-inside list-disc space-y-1 ml-6'>
              {validationErrors.map((error, i) => (
                <li key={i} className='typography-body2'>
                  {error}
                </li>
              ))}
            </ul>
          </Stack>
        </Alert>
      )}
      {mediaMapDraft.length === 0 ? (
        <span className='typography-body1 text-text-secondary'>{t('shared.mediaMaps.configuration.emptyState')}</span>
      ) : (
        <Stack direction='row' spacing={4} className='flex-wrap'>
          {mediaMapDraft.map((item) => (
            <MediaMapItemCard
              key={item.id}
              item={item}
              configurationId={configuration.id}
              onDelete={removeItemFromMediaMapDraft}
              onUpdate={updateItemInMediaMapDraft}
              showActionButtons={!hasJustSubmitted && canManageInsituData}
            />
          ))}
        </Stack>
      )}
      {canManageInsituData && (
        <Stack className='w-full items-end'>
          <Button
            disabled={!isDraftMediaMap || mediaMapDraft.length === 0 || hasJustSubmitted}
            loading={isSubmitting}
            onClick={handleSubmit}
            data-testid='media-map-submit-button'
            className='w-fit py-4 px-8'
          >
            {t('shared.mediaMaps.configuration.buttons.submit')}
          </Button>
        </Stack>
      )}
      {showAddDialog && (
        <SuspenseBoundary fallback={null}>
          <MediaMapItemDialog
            open={showAddDialog}
            onClose={() => setShowAddDialog(false)}
            configurationId={configuration.id}
            onAdd={addItemToMediaMapDraft}
            onUpdate={updateItemInMediaMapDraft}
          />
        </SuspenseBoundary>
      )}
      <ConfirmationDialog
        open={showClearDialog}
        header={t('shared.mediaMaps.configuration.clearChangesDialog.header')}
        body={t('shared.mediaMaps.configuration.clearChangesDialog.body')}
        CancelAction={
          <Button variant='outline' onClick={() => setShowClearDialog(false)}>
            {t('global.ui.buttons.cancel')}
          </Button>
        }
        PrimaryAction={
          <Button
            onClick={() => {
              clearMediaMapDraft();
              setShowClearDialog(false);
            }}
          >
            {t('global.ui.buttons.confirm')}
          </Button>
        }
      />
    </Stack>
  );
};

enum FormStatus {
  Draft = 'draft',
  Saved = 'saved',
  Waiting = 'waiting',
  Submitted = 'submitted',
  Error = 'error',
}

const getFormStatusFromConfiguration = (
  configuration: IndicatorProductSubscriptionConfiguration,
  isDraft: boolean,
  hasJustSubmitted: boolean,
): FormStatus => {
  if (hasJustSubmitted) {
    return FormStatus.Submitted;
  }
  if (isDraft) {
    return FormStatus.Draft;
  }

  const { status } = configuration;
  if (
    (
      [
        INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.VALIDATED,
        INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.APPROVED,
      ] as string[]
    ).includes(status)
  ) {
    return FormStatus.Saved;
  }

  if (status === INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.REJECTED) {
    return FormStatus.Error;
  }
  if (status === INDICATOR_PRODUCT_SUBSCRIPTION_CONFIGURATION_STATUS.WAITING) {
    return FormStatus.Waiting;
  }
  return FormStatus.Draft;
};

const MediaMapStatusPill = ({ status }: { status: FormStatus }) => {
  const { t } = useTranslation();

  const styles: Record<FormStatus, { color: string; backgroundColor: string; label: string }> = {
    [FormStatus.Draft]: {
      label: t('shared.mediaMaps.configuration.status.draft'),
      color: resolveThemeColor('warning-dark'),
      backgroundColor: resolveThemeColor('warning-light'),
    },
    [FormStatus.Submitted]: {
      label: t('shared.mediaMaps.configuration.status.submitted'),
      color: resolveThemeColor('success-dark'),
      backgroundColor: resolveThemeColor('secondary-50'),
    },
    [FormStatus.Saved]: {
      label: t('shared.mediaMaps.configuration.status.saved'),
      color: resolveThemeColor('success-dark'),
      backgroundColor: resolveThemeColor('success-light'),
    },
    [FormStatus.Waiting]: {
      label: t('shared.mediaMaps.configuration.status.waiting'),
      color: resolveThemeColor('warning-dark'),
      backgroundColor: resolveThemeColor('warning-light'),
    },
    [FormStatus.Error]: {
      label: t('shared.mediaMaps.configuration.status.error'),
      color: resolveThemeColor('error-dark'),
      backgroundColor: resolveThemeColor('error-light'),
    },
  };

  const style = styles[status as FormStatus];
  if (!style) {
    return null;
  }

  return (
    <Pill
      style={{ color: style.color, backgroundColor: style.backgroundColor }}
      size='large'
      className='typography-body1Semibold px-6'
    >
      {style.label}
    </Pill>
  );
};
